import { v4 as uuidv4 } from "uuid";
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useState, useRef } from "react";
import { AiSearchDocumentInfo, ChatExchange, LLMResponse } from "../api/types/chatTypes";
import { generateSasToken, getCitationFilePath } from "../api/endpoints/fileAPI";

type ConversationContextType = {
    sessionId: string;
    activeCitation: AiSearchDocumentInfo | undefined;
    setActiveCitation: (value: AiSearchDocumentInfo | undefined) => void;
    isAnalysisPanelTabActive: boolean;
    setIsAnalysisPanelTabActive: Dispatch<SetStateAction<boolean>>;
    chatHistory: ChatExchange[];
    setChatHistory: Dispatch<SetStateAction<ChatExchange[]>>;
    selectedAnswer?: LLMResponse;
    setSelectedAnswer?: Dispatch<SetStateAction<LLMResponse | undefined>>;
    onShowCitation: (citation: AiSearchDocumentInfo, newAnswer: LLMResponse) => void;
    hasMessage: boolean;
    embeddingTime: string | undefined;
    setEmbeddingTime: Dispatch<SetStateAction<string | undefined>>;
    aiSearchTime: string | undefined;
    setAiSearchTime: Dispatch<SetStateAction<string | undefined>>;
    foundSources?: number;
    setFoundSources: Dispatch<SetStateAction<number | undefined>>;
};

const ConversationContext = createContext<ConversationContextType | undefined>(undefined);

export const ConversationProvider = ({ children }: { children: ReactNode }) => {
    const activeCitationRef = useRef<AiSearchDocumentInfo | undefined>(undefined);
    const [, forceRender] = useState(0);
    const setActiveCitation = (value: AiSearchDocumentInfo | undefined) => {
        activeCitationRef.current = value;
        forceRender(x => x + 1); // trigger UI update when citation changes
    };

    const [isAnalysisPanelTabActive, setIsAnalysisPanelTabActive] = useState<boolean>(false);
    const [chatHistory, setChatHistory] = useState<ChatExchange[]>([]);
    const [selectedAnswer, setSelectedAnswer] = useState<LLMResponse>();

    // Current chat
    const [embeddingTime, setEmbeddingTime] = useState<string | undefined>();
    const [aiSearchTime, setAiSearchTime] = useState<string | undefined>();
    const [foundSources, setFoundSources] = useState<number | undefined>();

    const [sessionId, setSessionId] = useState<string>("");

    useEffect(() => {
        if (chatHistory.length === 0) setSessionId(uuidv4());
    }, [chatHistory]);

    const onShowCitation = async (citation: AiSearchDocumentInfo, _newAnswer: LLMResponse) => {
        const source = citation.original_file_blob_path;

        // Website sources: detect by URL pattern
        if (source.startsWith("http://") || source.startsWith("https://")) {
            window.open(source, "_blank", "noopener,noreferrer");
            return;
        }

        // Always use the full document path
        const parts = source.split(".");
        const extension = parts[parts.length - 1].toLowerCase();

        if (extension === "pdf") {
            // PDFs: use the content proxy path
            window.open(getCitationFilePath(source), "_blank", "noopener,noreferrer");
        } else {
            // Other files (docx, xlsx, etc.): get a SAS token viewer URL
            try {
                const viewerUrl = await generateSasToken(source);
                window.open(viewerUrl, "_blank", "noopener,noreferrer");
            } catch (error) {
                console.error("Failed to generate SAS token for document:", error);
            }
        }
    };

    const hasMessage = useMemo(() => chatHistory.length > 0, [chatHistory]);

    return (
        <ConversationContext.Provider
            value={{
                sessionId,
                activeCitation: activeCitationRef.current,
                setActiveCitation,
                isAnalysisPanelTabActive,
                setIsAnalysisPanelTabActive,
                chatHistory,
                setChatHistory,
                selectedAnswer,
                setSelectedAnswer,
                onShowCitation,
                hasMessage,
                embeddingTime,
                setEmbeddingTime,
                aiSearchTime,
                setAiSearchTime,
                foundSources,
                setFoundSources
            }}
        >
            {children}
        </ConversationContext.Provider>
    );
};

export const useConversation = () => {
    const context = useContext(ConversationContext);
    if (!context) throw new Error("useConversation must be used within a ConversationProvider");
    return context;
};
